/**
 * Browser-side HTTP client for the SidonIAssist BFF (`fetch` + session cookies).
 *
 * @remarks Uses same-origin relative URLs (`API_BASE_URL` empty). Errors normalize Dust vs generic shapes.
 */

import { API_ENDPOINTS } from "@/constants";
import { fetchWithAuthRedirect } from "@/lib/api/auth-redirect";
import { appErrorFromResponseBody } from "@/lib/errors/app-error";

import type {
  Tag,
  CguConfig,
  DbConversation,
  DbUser,
  UserQuotaStatus,
  DbConversationLabel,
  DustConversation,
  DbDocumentType,
  DbDefaultUserTag,
  EnrichedSidoniDocument,
  ShareConversationResponse,
  SharedConversation,
  ConversationQuestion,
} from "@/types";
import type { PublicServiceAnnouncement } from "@/types/schemas/service-announcement";

/** Empty string = same origin as the SPA. */
const API_BASE_URL = "";

/** JSON `fetch` helper with credentials and unified error extraction. */
class SidoniAssistApiClient {
  /** Low-level GET/POST/PATCH/DELETE with JSON body defaults. */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetchWithAuthRedirect(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Unknown error" }));

      throw appErrorFromResponseBody(response.status, errorData);
    }

    return response.json();
  }

  // ==================== REFERENCE TABLES ====================

  /** System catalog of document types for filters. */
  async getDocumentTypes(): Promise<DbDocumentType[]> {
    return this.request<DbDocumentType[]>(API_ENDPOINTS.DOCUMENT_TYPES);
  }

  /** Default tags suggested when provisioning users. */
  async getDefaultUserTags(): Promise<DbDefaultUserTag[]> {
    return this.request<DbDefaultUserTag[]>(API_ENDPOINTS.DEFAULT_USER_TAGS);
  }

  // ==================== USERS ====================

  /** Current user row (session + lazy provisioning on dust-api). */
  async getUser(): Promise<DbUser> {
    return this.request<DbUser>(API_ENDPOINTS.CURRENT_USER);
  }

  /** Required CGU version and document URLs (matches `getUser().requiredCguVersion` semantics). */
  async getCguConfig(): Promise<CguConfig> {
    return this.request<CguConfig>(API_ENDPOINTS.CGU_CONFIG);
  }

  /** Record CGU acceptance; server rejects version mismatch. */
  async acceptCgu(acceptedVersion: string): Promise<DbUser> {
    return this.request<DbUser>(API_ENDPOINTS.USER_CGU_ACCEPTANCE, {
      method: "PATCH",
      body: JSON.stringify({ acceptedVersion }),
    });
  }

  /** Quota de requêtes sur la période active (`null` si aucune période / politique). */
  async getMyQuota(): Promise<UserQuotaStatus | null> {
    return this.request<UserQuotaStatus | null>(API_ENDPOINTS.USER_QUOTA_ME);
  }

  /** Annonce de service active, ou `null` si désactivée. */
  async getServiceAnnouncement(): Promise<PublicServiceAnnouncement | null> {
    const data = await this.request<{
      announcement: PublicServiceAnnouncement | null;
    }>(API_ENDPOINTS.SERVICE_ANNOUNCEMENT);
    return data.announcement;
  }

  // ==================== CONVERSATIONS ====================

  /** List Sidoni conversations for the session user. */
  async getConversations(): Promise<DbConversation[]> {
    return this.request<DbConversation[]>(API_ENDPOINTS.CONVERSATIONS_DB);
  }

  /** Sidoni conversation row by numeric id. */
  async getConversation(id: number): Promise<DbConversation> {
    return this.request<DbConversation>(API_ENDPOINTS.CONVERSATION(id));
  }

  /** Fire-and-forget durable registry upsert (idempotent server-side). */
  async patchConversationQuestionsRegistry(
    dustConversationId: string,
    questions: ConversationQuestion[]
  ): Promise<{ updated: boolean; questionsSyncedAt: string | null }> {
    return this.request<{ updated: boolean; questionsSyncedAt: string | null }>(
      API_ENDPOINTS.CONVERSATION_QUESTIONS_PATCH(dustConversationId),
      {
        method: "PATCH",
        body: JSON.stringify({ questions }),
      }
    );
  }

  /** Next unsynced mini-batch (lastActivityAt DESC) — server reads Dust and persists JSONB. */
  async backfillConversationQuestionsChunk(limit = 5): Promise<{
    items: { conversationId: string; questions: ConversationQuestion[] }[];
    remainingCount: number;
    failedCount: number;
  }> {
    return this.request(API_ENDPOINTS.CONVERSATION_QUESTIONS_BACKFILL_CHUNK, {
      method: "POST",
      body: JSON.stringify({ limit }),
    });
  }

  /** Insert sidecar row for a Dust conversation id. */
  async createConversation(
    dustConversationId: string,
    data: {
      title?: string;
      startedAt?: string;
      lastActivityAt?: string;
    }
  ): Promise<DbConversation> {
    return this.request<DbConversation>(API_ENDPOINTS.CONVERSATIONS_DB, {
      method: "POST",
      body: JSON.stringify({
        dustConversationId,
        ...data,
      }),
    });
  }

  /** Patch metadata fields. */
  async updateConversation(
    id: number,
    data: {
      title?: string;
    }
  ): Promise<DbConversation> {
    return this.request<DbConversation>(API_ENDPOINTS.CONVERSATION(id), {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  /** Delete Sidoni row (Dust thread lifecycle is separate). */
  async deleteConversation(
    id: number
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      API_ENDPOINTS.CONVERSATION(id),
      {
        method: "DELETE",
      }
    );
  }

  // ==================== CONVERSATION SHARING ====================

  /**
   * Publish conversation: allocate share token if missing (idempotent).
   *
   * @returns Token and absolute share URL from server.
   */
  async shareConversation(dbId: number): Promise<ShareConversationResponse> {
    return this.request<ShareConversationResponse>(
      API_ENDPOINTS.CONVERSATION_SHARE(dbId),
      { method: "POST" }
    );
  }

  /** Unpublish while retaining token for future re-share. */
  async unshareConversation(dbId: number): Promise<{ isPublic: false }> {
    return this.request<{ isPublic: false }>(
      API_ENDPOINTS.CONVERSATION_SHARE(dbId),
      { method: "DELETE" }
    );
  }

  /** Public share payload (no cookie auth). */
  async getSharedConversation(token: string): Promise<SharedConversation> {
    return this.request<SharedConversation>(
      API_ENDPOINTS.SHARED_CONVERSATION(token)
    );
  }

  // ==================== CONVERSATION LABELS ====================

  /** Labels (document type + user tag) for a conversation. */
  async getConversationLabels(
    conversationId: number
  ): Promise<DbConversationLabel[]> {
    return this.request<DbConversationLabel[]>(
      API_ENDPOINTS.CONVERSATION_LABELS(conversationId)
    );
  }

  /** Attach catalog document-type tag. */
  async addDocumentTypeLabel(
    conversationId: number,
    tagId: string
  ): Promise<DbConversationLabel> {
    return this.request<DbConversationLabel>(
      API_ENDPOINTS.CONVERSATION_LABELS(conversationId),
      {
        method: "POST",
        body: JSON.stringify({ tagId, labelType: "documentType" }),
      }
    );
  }

  /** Attach user-owned custom tag. */
  async addUserTagLabel(
    conversationId: number,
    tagId: string
  ): Promise<DbConversationLabel> {
    return this.request<DbConversationLabel>(
      API_ENDPOINTS.CONVERSATION_LABELS(conversationId),
      {
        method: "POST",
        body: JSON.stringify({ tagId, labelType: "userTag" }),
      }
    );
  }

  /** Detach document-type label. */
  async removeDocumentTypeLabel(
    conversationId: number,
    tagId: string
  ): Promise<void> {
    await this.request<void>(
      API_ENDPOINTS.CONVERSATION_LABEL(conversationId, "documentType", tagId),
      {
        method: "DELETE",
      }
    );
  }

  /** Detach user tag label. */
  async removeUserTagLabel(
    conversationId: number,
    tagId: string
  ): Promise<void> {
    await this.request<void>(
      API_ENDPOINTS.CONVERSATION_LABEL(conversationId, "userTag", tagId),
      {
        method: "DELETE",
      }
    );
  }

  /** Gatekeeper precisions persisted for a Dust conversation. */
  async getGatekeeperPrecisions(dustConversationId: string): Promise<{
    items: {
      dustMessageId: string;
      gatekeeperPrecisions: {
        clarification: string;
        userReply: string;
      }[];
    }[];
  }> {
    return this.request(
      API_ENDPOINTS.CONVERSATION_GATEKEEPER_PRECISIONS(dustConversationId)
    );
  }

  /** Upsert gatekeeper precisions for a qualified user message. */
  async upsertGatekeeperPrecisions(
    dustConversationId: string,
    body: {
      dustMessageId: string;
      gatekeeperPrecisions: {
        clarification: string;
        userReply: string;
      }[];
    }
  ): Promise<{ success: true }> {
    return this.request(
      API_ENDPOINTS.CONVERSATION_GATEKEEPER_PRECISIONS(dustConversationId),
      {
        method: "PUT",
        body: JSON.stringify(body),
      }
    );
  }

  /**
   * Load Dust conversation by `sId` and ensure Sidoni sidecar for the session user.
   */
  async getDustConversation(
    dustConversationId: string
  ): Promise<DustConversation> {
    const url = `${API_ENDPOINTS.CONVERSATIONS}/${dustConversationId}`;
    return this.request<DustConversation>(url);
  }

  /**
   * Cancel in-flight generation. Empty `messageIds` cancels the in-progress agent
   * messages of that conversation — sous unicité du cycle, c'est le run courant.
   */
  async cancelConversation(
    dustConversationId: string,
    agentMessageId?: string | null
  ): Promise<{
    success: boolean;
    cancelledMessageIds?: string[];
    confirmed?: boolean;
    terminalStatuses?: Record<string, string | undefined>;
  }> {
    const messageIds = agentMessageId?.trim() ? [agentMessageId.trim()] : [];
    return this.request(
      `${API_ENDPOINTS.CONVERSATIONS}/${dustConversationId}/cancel`,
      {
        method: "POST",
        body: JSON.stringify({ messageIds }),
      }
    );
  }

  /** Libère le verrou d'unicité du cycle chat (abandon hors SSE). */
  async abandonChatCycle(): Promise<{ ok: boolean }> {
    return this.request(API_ENDPOINTS.CHAT_ABANDON_CYCLE, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  /** Stored feedbacks for the current user in a Dust conversation. */
  async getConversationFeedbacks(dustConversationId: string): Promise<{
    feedbacks: {
      dustMessageId: string;
      thumbDirection: "up" | "down";
      feedbackContent: string | null;
      motifs: Array<{ code: string; label: string }>;
    }[];
  }> {
    return this.request(
      API_ENDPOINTS.CONVERSATION_FEEDBACKS(dustConversationId)
    );
  }

  /** Thumbs (+ optional motifs/comment) feedback on an assistant message. */
  async postMessageFeedback(
    dustConversationId: string,
    messageId: string,
    body: {
      thumbDirection: "up" | "down";
      feedbackContent?: string | null;
      motifs?: Array<{ code: string; label: string }>;
    }
  ): Promise<{ success: true }> {
    return this.request<{ success: true }>(
      API_ENDPOINTS.CONVERSATION_MESSAGE_FEEDBACKS(
        dustConversationId,
        messageId
      ),
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    );
  }

  /** Clear user feedback for an assistant message. */
  async deleteMessageFeedback(
    dustConversationId: string,
    messageId: string
  ): Promise<{ success: true }> {
    return this.request<{ success: true }>(
      API_ENDPOINTS.CONVERSATION_MESSAGE_FEEDBACKS(
        dustConversationId,
        messageId
      ),
      { method: "DELETE" }
    );
  }

  /** Dust agent configurations for picker UI. */
  async getAssistants(): Promise<
    Array<{
      id: number;
      sId: string;
      name: string;
      description: string;
      pictureUrl?: string;
      status: string;
      [key: string]: unknown;
    }>
  > {
    return this.request<
      Array<{
        id: number;
        sId: string;
        name: string;
        description: string;
        pictureUrl?: string;
        status: string;
        [key: string]: unknown;
      }>
    >(API_ENDPOINTS.ASSISTANTS);
  }

  // ==================== SIDONI DOCUMENTS ====================

  /**
   * Fetch enriched Sidoni document metadata by an array of document IDs.
   * Joins with attachments and resolves meta fields (titre, url, etc.)
   */
  async getSidoniDocuments(ids: number[]): Promise<EnrichedSidoniDocument[]> {
    return this.request<EnrichedSidoniDocument[]>(
      API_ENDPOINTS.SIDONI_DOCUMENTS_BY_IDS,
      {
        method: "POST",
        body: JSON.stringify({ ids }),
      }
    );
  }

  /**
   * Fetch enriched Sidoni document metadata by an array of document noms (slugs).
   * Fallback when the LLM returns a nom instead of a numeric id.
   */
  async getSidoniDocumentsByNoms(
    noms: string[]
  ): Promise<EnrichedSidoniDocument[]> {
    return this.request<EnrichedSidoniDocument[]>(
      API_ENDPOINTS.SIDONI_DOCUMENTS_BY_NOMS,
      {
        method: "POST",
        body: JSON.stringify({ noms }),
      }
    );
  }

  /**
   * Resolve noms as piece_jointe.nom, return parent docs. One result per nom (or null), same order.
   * Fallback when by-noms finds nothing.
   */
  async getSidoniDocumentsByPjNoms(
    noms: string[]
  ): Promise<(EnrichedSidoniDocument | null)[]> {
    return this.request<(EnrichedSidoniDocument | null)[]>(
      API_ENDPOINTS.SIDONI_DOCUMENTS_BY_PJ_NOMS,
      {
        method: "POST",
        body: JSON.stringify({ noms }),
      }
    );
  }

  // ==================== SIDONI DOCUMENTS (PUBLIC — share token) ====================

  /**
   * Public variant of getSidoniDocuments. Uses plain fetch (no auth redirect).
   * Requires a valid share token in exchange for document data.
   */
  async getSidoniDocumentsPublic(
    ids: number[],
    shareToken: string
  ): Promise<EnrichedSidoniDocument[]> {
    const url = API_ENDPOINTS.SHARE_DOCUMENTS_BY_IDS(shareToken);
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  /**
   * Public variant of getSidoniDocumentsByNoms. Uses plain fetch (no auth redirect).
   */
  async getSidoniDocumentsByNomsPublic(
    noms: string[],
    shareToken: string
  ): Promise<EnrichedSidoniDocument[]> {
    const url = API_ENDPOINTS.SHARE_DOCUMENTS_BY_NOMS(shareToken);
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ noms }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  /**
   * Public variant of getSidoniDocumentsByPjNoms. Uses plain fetch (no auth redirect).
   */
  async getSidoniDocumentsByPjNomsPublic(
    noms: string[],
    shareToken: string
  ): Promise<(EnrichedSidoniDocument | null)[]> {
    const url = API_ENDPOINTS.SHARE_DOCUMENTS_BY_PJ_NOMS(shareToken);
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ noms }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  // ==================== USER TAGS ====================

  /** Custom tags for current user. */
  async getUserTags(): Promise<Tag[]> {
    return this.request(API_ENDPOINTS.USER_TAGS_ME);
  }

  /** Create user tag with stable id + presentation. */
  async createUserTag(tag: Tag): Promise<Tag> {
    return this.request(API_ENDPOINTS.USER_TAGS_ME, {
      method: "POST",
      body: JSON.stringify({
        tagId: tag.id,
        label: tag.label,
        color: tag.color,
      }),
    });
  }

  /** Delete tag by stable id. */
  async deleteUserTag(tagId: string): Promise<void> {
    await this.request(API_ENDPOINTS.USER_TAG_ME(tagId), {
      method: "DELETE",
    });
  }

  /** Update tag color by stable id. */
  async updateUserTagColor(tagId: string, color: string): Promise<Tag> {
    return this.request(API_ENDPOINTS.USER_TAG_ME(tagId), {
      method: "PATCH",
      body: JSON.stringify({ color }),
    });
  }
}

/**
 * Shared singleton for the SPA data layer.
 *
 * @remarks Import `apiClient` instead of instantiating `SidoniAssistApiClient`.
 */
export const apiClient = new SidoniAssistApiClient();
